import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart3, Settings2 } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../ui/tabs';
import cptConfigService, {
    CPTConfigValue,
} from '../../../services/api/CPTAnalytics/CPTConfigService';
import LoadingRing from '../../ui-components/LoadingRing';
import CPTConfigTab from './CPTConfigTab';
import CPTAnalyticsTab from './CPTAnalyticsTab';
import {
    EVENT_KEYS,
    trackLoad,
} from '../../../services/api/trackingService';

export default function CPTAnalyticsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') === 'config' ? 'config' : 'analytics';

    const [config, setConfig] = useState<CPTConfigValue | null>(null);
    const [loadingConfig, setLoadingConfig] = useState(true);

    const handleTabChange = useCallback(
        (value: string) => {
            setSearchParams({ tab: value }, { replace: true });
        },
        [setSearchParams]
    );

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const data = await cptConfigService.getCPTConfig();
                setConfig(data);
            } catch {
                // Config not found — will use empty defaults
            } finally {
                setLoadingConfig(false);
            }
        };

        fetchConfig();
    }, []);

    useEffect(() => {
        trackLoad(EVENT_KEYS.viewCptAnalytics, {});
    }, []);

    return (
        <div className="container mx-auto max-w-5xl space-y-8 px-4 py-8">
            <div>
                <h1 className="text-3xl font-bold tracking-tight">
                    CPT Analytics
                </h1>
                <p className="mt-1 text-base text-muted-foreground">
                    Department overview of CPT activity, RVU trends, and revenue
                    performance.
                </p>
            </div>

            <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList>
                    <TabsTrigger value="analytics">
                        <BarChart3 className="mr-1.5 h-4 w-4" />
                        Analytics
                    </TabsTrigger>
                    <TabsTrigger value="config">
                        <Settings2 className="mr-1.5 h-4 w-4" />
                        Config
                    </TabsTrigger>
                </TabsList>

                <div className="mt-4 border-t border-subtle" />

                <TabsContent value="analytics" className="mt-6">
                    <CPTAnalyticsTab config={config} loadingConfig={loadingConfig} onGoToConfig={() => handleTabChange('config')} />
                </TabsContent>

                <TabsContent value="config" className="mt-6">
                    {loadingConfig ? (
                        <div className="flex justify-center py-12">
                            <LoadingRing size="large" />
                        </div>
                    ) : (
                        <CPTConfigTab
                            config={config}
                            onConfigSaved={setConfig}
                        />
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}
